import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Mail, RefreshCw, PackageCheck, Pencil, Printer } from "lucide-react";
import EditRequestDialog from "@/components/EditRequestDialog";
import PrintExportButtons from "@/components/PrintExportButtons";
import { groupBorder, groupBg } from "@/lib/lineItems";

const STATUS_COLORS = {
  pending: "bg-yellow-500",
  dispatched: "bg-orange-600",
  in_transit: "bg-blue-600",
  completed: "bg-emerald-600",
  cancelled: "bg-zinc-500",
};

function Party({ title, p }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">{title}</div>
      <div className="font-semibold text-base">{p?.company}</div>
      <div className="text-sm text-muted-foreground">{p?.contact_name}</div>
      <div className="text-sm whitespace-pre-line">{p?.address}</div>
      {p?.email && <div className="font-mono text-xs mt-1">{p.email}</div>}
      {p?.phone && <div className="font-mono text-xs text-muted-foreground">{p.phone}</div>}
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [recvDate, setRecvDate] = useState("");
  const [recvNotes, setRecvNotes] = useState("");
  const [recvSaving, setRecvSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [labelsEnabled, setLabelsEnabled] = useState(true);
  const [printHtml, setPrintHtml] = useState("");
  const printRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/requests/${id}`);
    setR(data);
    if (data.receival?.received_date) setRecvDate(data.receival.received_date);
    else setRecvDate(new Date().toISOString().slice(0, 10));
    setRecvNotes(data.receival?.notes || "");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    api.get("/company")
      .then(({ data }) => setLabelsEnabled(data.labels_enabled !== false))
      .catch(() => setLabelsEnabled(true));
  }, []);
  useEffect(() => {
    // Fetch the printable (email-styled) version so Print / Download / PDF render the same layout as the email.
    api.get(`/requests/${id}/print`, { responseType: "text" })
      .then(({ data }) => setPrintHtml(data))
      .catch(() => setPrintHtml(""));
  }, [id]);

  const resend = async () => {
    try {
      await api.post(`/requests/${id}/resend`);
      toast.success("Notifications resent");
      load();
    } catch { toast.error("Resend failed"); }
  };

  const setStatus = async (status) => {
    await api.patch(`/requests/${id}/status`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const confirmReceival = async () => {
    setRecvSaving(true);
    try {
      await api.patch(`/requests/${id}/receive`, {
        received: true,
        received_date: recvDate,
        notes: recvNotes,
      });
      toast.success("Receival logged", { description: "Supplier & courier notified goods were received in" });
      load();
    } catch {
      toast.error("Failed to record receival");
    } finally {
      setRecvSaving(false);
    }
  };

  const clearReceival = async () => {
    setRecvSaving(true);
    try {
      await api.patch(`/requests/${id}/receive`, { received: false });
      toast.message("Receival cleared");
      load();
    } finally {
      setRecvSaving(false);
    }
  };

  if (!r) return <div className="text-sm text-muted-foreground">Loading...</div>;

  const refFields = [
    ["PO Number", r.references?.po_number],
    ["Invoice #", r.references?.invoice_number],
    ["Job #", r.references?.job_number],
    ["Customer Ref", r.references?.customer_reference],
    ["Internal Ref", r.references?.internal_reference],
  ].filter(([, v]) => v);

  return (
    <div className="space-y-6">
      <Link to="/requests" data-testid="back-to-list" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground hover:text-foreground no-print">
        <ArrowLeft className="h-3.5 w-3.5" /> All requests
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4 no-print">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">{r.request_type} Request</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-2 font-mono">{r.reference_number}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap w-full lg:w-auto">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold px-3 py-2 border-2 border-border">
            <span className={`h-2 w-2 ${STATUS_COLORS[r.status] || "bg-zinc-400"}`} />
            {r.status}
          </span>
          <Select onValueChange={setStatus}>
            <SelectTrigger data-testid="update-status" className="w-full sm:w-44 rounded-none h-10 border-2"><SelectValue placeholder="Update status..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="dispatched">Dispatched</SelectItem>
              <SelectItem value="in_transit">In Transit</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={resend} data-testid="resend-button" variant="outline" className="rounded-none border-2 h-10 flex-1 sm:flex-none">
            <RefreshCw className="h-3.5 w-3.5 mr-2" /> Resend
          </Button>
          {labelsEnabled && (
            <Button onClick={() => window.open(`/requests/${id}/labels`, "_blank")} data-testid="print-labels-button" variant="outline" className="rounded-none border-2 h-10 flex-1 sm:flex-none">
              <Printer className="h-3.5 w-3.5 mr-2" /> Labels
            </Button>
          )}
          <PrintExportButtons
            htmlSource={printHtml}
            targetRef={printRef}
            fileName={`request-${r.reference_number}`}
            testidPrefix="request-export"
            size="md"
          />
          <Button onClick={() => setEditOpen(true)} data-testid="edit-request-button" className="rounded-none bg-foreground text-background hover:bg-foreground/85 h-10 uppercase tracking-[0.15em] text-xs font-bold flex-1 sm:flex-none">
            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 screen-only">
        <div className="lg:col-span-8 space-y-6">
          <div className="border-2 border-border bg-card p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Party title="Pickup From" p={r.pickup} />
            <Party title="Deliver To" p={r.delivery} />
          </div>

          {refFields.length > 0 && (
            <div className="border-2 border-border bg-card">
              <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">References</div>
              <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                {refFields.map(([l, v]) => (
                  <div key={l}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{l}</div>
                    <div className="font-mono text-sm mt-1">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {r.items?.length > 0 && (
            <div className="border-2 border-border bg-card">
              <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Line Items</div>
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Item #</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Description</th>
                    <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Qty</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Pkg Type</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Dims</th>
                    <th className="text-right px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Weight</th>
                    <th className="text-center px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Group</th>
                  </tr>
                </thead>
                <tbody>
                  {r.items.map((it, i) => {
                    const border = groupBorder(it.package_group);
                    const bg = groupBg(it.package_group);
                    return (
                      <tr key={i} className="border-t border-border">
                        <td className={`px-4 py-2 font-mono border-l-4 ${border || "border-transparent"}`}>{it.item_number || "—"}</td>
                        <td className="px-4 py-2">{it.description}</td>
                        <td className="px-4 py-2 text-right font-mono">{it.quantity}</td>
                        <td className="px-4 py-2">{it.package_type || "—"}</td>
                        <td className="px-4 py-2 font-mono text-xs">{it.dimensions || "—"}</td>
                        <td className="px-4 py-2 text-right font-mono">{it.weight ?? "—"}</td>
                        <td className="px-4 py-2 text-center text-xs">
                          {it.package_group ? <span className={`inline-block px-2 py-0.5 text-white font-bold ${bg}`}>Pkg {it.package_group}</span> : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {r.instructions && (
            <div className="border-2 border-border bg-card p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Special Instructions</div>
              <p className="text-sm whitespace-pre-line">{r.instructions}</p>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className={`border-2 ${r.receival?.received ? "border-emerald-600 bg-emerald-50 dark:bg-emerald-950" : "border-orange-600 bg-orange-50 dark:bg-orange-950"}`}>
            <div className="px-6 py-3 border-b-2 border-current/20 text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2">
              <PackageCheck className="h-3.5 w-3.5" /> Goods Received Back
            </div>
            <div className="p-6 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  data-testid="receival-checkbox"
                  checked={!!r.receival?.received}
                  onCheckedChange={(v) => {
                    if (v && !r.receival?.received) confirmReceival();
                    else if (!v && r.receival?.received) clearReceival();
                  }}
                  disabled={recvSaving}
                  className="mt-0.5 rounded-none border-2 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                />
                <div className="text-sm">
                  <div className="font-bold">Mark as received in</div>
                  <div className="text-xs text-muted-foreground">Tick when the goods have physically returned. This will email the supplier and courier.</div>
                </div>
              </label>

              {!r.receival?.received && (
                <>
                  <div className="space-y-2">
                    <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Receival date *</Label>
                    <Input
                      type="date"
                      data-testid="receival-date"
                      value={recvDate}
                      onChange={(e) => setRecvDate(e.target.value)}
                      className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Notes (optional)</Label>
                    <Textarea
                      data-testid="receival-notes"
                      value={recvNotes}
                      onChange={(e) => setRecvNotes(e.target.value)}
                      rows={2}
                      placeholder="e.g., 2 cartons short, one damaged..."
                      className="rounded-none border-2"
                    />
                  </div>
                  <Button
                    onClick={confirmReceival}
                    disabled={recvSaving || !recvDate}
                    data-testid="receival-confirm-button"
                    className="w-full h-10 rounded-none bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-[0.2em] text-xs font-bold"
                  >
                    <PackageCheck className="h-3.5 w-3.5 mr-2" />
                    {recvSaving ? "Sending..." : "Confirm & Notify"}
                  </Button>
                </>
              )}

              {r.receival?.received && (
                <div className="text-sm space-y-1 border-t border-emerald-600/30 pt-3">
                  <div><span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Received on</span></div>
                  <div className="font-mono text-base">{r.receival.received_date}</div>
                  <div className="text-xs text-muted-foreground">by {r.receival.received_by} &middot; {new Date(r.receival.received_at).toLocaleString()}</div>
                  {r.receival.notes && (
                    <div className="mt-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Notes</div>
                      <div className="text-sm whitespace-pre-line">{r.receival.notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="border-2 border-border bg-card p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Courier</div>
            <div className="font-semibold mt-2">{r.courier?.company}</div>
            <div className="font-mono text-xs text-muted-foreground">{r.courier?.email}</div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Priority</div>
            <div className="mt-1 text-sm uppercase font-bold">{r.priority}</div>
          </div>

          <div className="border-2 border-border bg-card">
            <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
              <Mail className="h-3.5 w-3.5" /> Email Log
            </div>
            <div className="divide-y divide-border">
              {(r.email_log || []).length === 0 && <div className="p-4 text-sm text-muted-foreground">No emails sent yet.</div>}
              {(r.email_log || []).map((e, i) => (
                <div key={i} className="p-4 text-xs space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="uppercase tracking-[0.15em] font-bold">{e.role}{e.resent ? " (resent)" : ""}</span>
                    <span className={e.error ? "text-red-600" : "text-emerald-600"}>{e.error ? "Failed" : "Sent"}</span>
                  </div>
                  <div className="font-mono truncate">{e.to}</div>
                  <div className="text-muted-foreground">{new Date(e.at).toLocaleString()}</div>
                  {e.error && <div className="text-red-600">{e.error}</div>}
                </div>
              ))}
            </div>
          </div>

          <div className="border-2 border-border bg-card">
            <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Timeline</div>
            <div className="p-6 space-y-3">
              {(r.timeline || []).map((t, i) => (
                <div key={i} className="flex gap-3">
                  <div className="h-2 w-2 mt-1.5 bg-orange-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold uppercase tracking-wider">{t.status}</div>
                    <div className="text-xs text-muted-foreground">{new Date(t.at).toLocaleString()} · {t.by || "system"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* Print / PDF / Download render this email-styled layout instead of the on-screen cards. */}
      <div
        ref={printRef}
        data-testid="printable-request"
        className="print-only"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: printHtml }}
      />

      <EditRequestDialog open={editOpen} onOpenChange={setEditOpen} request={r} onSaved={load} />
    </div>
  );
}
